import {
  MakeReceivablePayment,
  MakeReceivablePaymentVariables,
} from "_graphql/mutation/__generated__/MakeReceivablePayment";
import {
  useTreatyClaim,
  useTreatyClaimShare,
} from "hooks/data/use-treaty-claims";

import { LocationGenerics } from "router/location";
import { Modal } from "components";
import { ReceivablePaymentForm } from "components/forms";
import { makeReceivablePayment } from "_graphql/visal/mutations/treaty";
import numeral from "numeral";
import toast from "react-hot-toast";
import { useAppLocation } from "_graphql/cache/auth";
import { useEffect } from "react";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-location";
import { useTreatyClaimPayments } from "hooks/data/use-treaties";
import { wrapClick } from "utils";

export default function CreateReceivablePaymentContainer({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
}) {
  const { params } = useAppLocation();
  const navigate = useNavigate<LocationGenerics>();
  const { payments } = useTreatyClaimPayments({
    participantId: params.share || "",
    treatyClaimId: params.claim || "",
  });
  const { claim, loading: loadingClaim } = useTreatyClaim({
    treatyClaimId: params.claim || "",
  });
  const { loading: fetchigOffer, share } = useTreatyClaimShare({
    participantId: params.share,
    treatyClaimId: params.claim || "",
  });

  const [recordPayment, { loading }] = useMutation<
    MakeReceivablePayment,
    MakeReceivablePaymentVariables
  >(makeReceivablePayment, {
    refetchQueries: ["TreatyClaims", "TreatyClaim", "GetTreatyClaimPayments"],
  });

  const form = useFormik<any>({
    initialValues: {
      payment_type: "",
      auto_payment_receipt: false,
      amountToPay: 0,
      amountPaidSoFar: 0,
      amountToPayOriginal: 0,
      layer_limit: "",
      installment_type: "",
      payment_from: {
        bank_name: "",
        cheque_number: "",
        date_on_cheque: "",
      },
      offer_payment_comment: "",
      payment_to: "",
      payment_details: {
        payment_amount: 0,
        currency: "",
        conversion: {
          rate: 1,
          currency: "",
          addExchangeRate: false,
        },
        payments,
      },
    },
    onSubmit: (value) => {
      recordPayment({
        variables: {
          treatyId: claim?.treaty?.treaty_id || "",
          receivablePayments: {
            payment_amount: value.payment_details.payment_amount,
            payment_detail: JSON.stringify({
              currency: value.payment_details.currency,
              payment_type: value.payment_type,
              payment_from: {
                cheque_number: value.payment_from.cheque_number
                  ? value.payment_from.cheque_number
                  : "N/A",
                bank_name: value.payment_from.bank_name,
                date_on_cheque: value.payment_from.date_on_cheque
                  ? value.payment_from.date_on_cheque
                  : "N/A",
              },
              payment_to: value.payment_to,
              conversion: {
                rate: value.payment_details.conversion.rate,
                currency:
                  value?.payment_details?.currency ||
                  value.payment_details.conversion.currency ||
                  "N/A",
                addExchangeRate:
                  value.payment_details.conversion.addExchangeRate,
              },
            }),
            treaty_claimstreaty_claim_id: params.claim,
            treaty_participation_id: params.share || "",
            uuid:
              JSON.parse(claim?.treaty?.layer_limit || "[]").at(
                Number(claim?.layer_number) - 1
              )?.uuid || "",
          },
        },
      })
        .then(() => {
          setOpen(false);
          toast.success("Payment recorded successfully");
        })
        .catch((error) => {
          toast.error(`Failed to record payment: ${error.message}`);
        });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  useEffect(() => {
    if (share && claim) {
      const allPastpayments = payments.reduce(
        (acc, payment) => acc + (payment?.payment_amount || 0),
        0
      );

      form.setFieldValue("payment_details.currency", claim?.treaty?.currency);
      form.setFieldValue(
        "payment_details.conversion.currency",
        claim?.treaty?.currency
      );

      const amountToPayRaw =
        numeral(share?.claim_payment_share).subtract(allPastpayments).value() ||
        0;
      const convertedAmount =
        amountToPayRaw <= 0
          ? 0
          : numeral(amountToPayRaw)
            .multiply(form.values.payment_details.conversion.rate)
            .value();
      // Only set if different
      if (form.values.amountToPay !== convertedAmount) {
        form.setFieldValue("amountToPay", convertedAmount);
        form.setFieldValue("amountToPayOriginal", amountToPayRaw);
        form.setFieldValue("amountPaidSoFar", allPastpayments);
        form.setFieldValue("payment_details.payment_amount", 0); // if needed
      }
    }
  }, [payments, share, form.values.payment_details.conversion.rate, claim]);

  return (
    <Modal
      open={open}
      setOpen={() => {
        setOpen(false);
        navigate({
          search(prev) {
            return {
              ...prev,
              rate: undefined,
              modal: undefined,
            };
          },
        });
      }}
      title="Record Claim Payment"
      loading={fetchigOffer || loadingClaim}
      description="Record a payment for a treaty claim. This will update the treaty's receivable balance."
      renderActions={() => (
        <>
          <button
            type="button"
            disabled={loading}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            onClick={wrapClick(form.handleSubmit)}
          >
            {loading ? "please wait..." : "Record Payment"}
          </button>
        </>
      )}
    >
      {/* {JSON.stringify(form.errors, null, 2)} */}
      <ReceivablePaymentForm form={form} claim={claim} share={share} />
    </Modal>
  );
}
